import type { GisSite, SiteStatus } from '../api/types'

// Worst-status-first within a mandal — overdue and never-surveyed are the accountability gaps.
const STATUS_SEVERITY: Record<SiteStatus, number> = { overdue: 0, never_surveyed: 1, in_progress: 2, surveyed: 3 }

export interface OfficerMandalGroup {
  /** Raw mandal key — render via prettyName(). */
  mandal: string
  sites: GisSite[]
  surveyed: number
  total: number
}

export interface OfficerAccountability {
  total: number
  surveyed: number
  inProgress: number
  overdue: number
  never: number
  /** surveyed / total across all the officer's sites. */
  completionPct: number
  mandalCount: number
  /** The officer's sites grouped by mandal, worst-mandal-first. */
  groups: OfficerMandalGroup[]
}

interface Tally {
  surveyed: number
  inProgress: number
  overdue: number
  never: number
}

/** Count sites by survey status. */
function tally(sites: GisSite[]): Tally {
  const c: Tally = { surveyed: 0, inProgress: 0, overdue: 0, never: 0 }
  for (const s of sites) {
    if (s.status === 'surveyed') {
      c.surveyed += 1
    } else if (s.status === 'in_progress') {
      c.inProgress += 1
    } else if (s.status === 'overdue') {
      c.overdue += 1
    } else {
      c.never += 1
    }
  }
  return c
}

/**
 * One officer's site accountability across the district — the data behind the officer drawer. Filters
 * the bulk sites payload by `officerId` (backend request #1), tallies statuses, and groups the sites
 * by mandal worst-first (lowest surveyed share), sites worst-status-first within each. Pure: no fetch.
 */
export function officerAccountability(sites: GisSite[], officerId: string): OfficerAccountability {
  const mine = sites.filter((s) => s.officerId === officerId)
  const byMandal = new Map<string, GisSite[]>()
  for (const s of mine) {
    const list = byMandal.get(s.mandal) ?? []
    list.push(s)
    byMandal.set(s.mandal, list)
  }
  const groups: OfficerMandalGroup[] = [...byMandal.entries()].map(([mandal, list]) => ({
    mandal,
    surveyed: list.filter((s) => s.status === 'surveyed').length,
    total: list.length,
    sites: [...list].sort((a, b) => STATUS_SEVERITY[a.status] - STATUS_SEVERITY[b.status]),
  }))
  groups.sort((a, b) => a.surveyed / a.total - b.surveyed / b.total || a.mandal.localeCompare(b.mandal))

  const c = tally(mine)
  const total = mine.length
  return {
    total,
    surveyed: c.surveyed,
    inProgress: c.inProgress,
    overdue: c.overdue,
    never: c.never,
    completionPct: total > 0 ? Math.round((c.surveyed / total) * 100) : 0,
    mandalCount: byMandal.size,
    groups,
  }
}
